/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState } from 'react';
import { getRequestOptions } from '../helpers/Fetchwrapper';
import { KTSVG } from '../../_metronic/helpers';
import { Col, Row, Table } from 'react-bootstrap-v5';
import { useParams, Link } from 'react-router-dom';
import 'react-dropzone-uploader/dist/styles.css';
import Dropzone from 'react-dropzone-uploader';
import Swal from 'sweetalert2';
import showLoader from '../helpers/ShowLoader';
import axios, { AxiosError, AxiosResponse } from 'axios';
import validationErrorMsg from '../helpers/ValidationErrorMsg';

interface AdjustmentRow {
    id_item_stock_adjustment_rows: number;
    id_items: number;
    stock_quantity: number;
    physical_quantity: number;
    average_rate: number;
    remarks: string | null;
    item: {
        id_items: number;
        itemName: string;
        itemCode: string;
        unit?: { unitName: string };
        item_category?: { categoryName: string };
        itemCategory?: { categoryName: string };
    };
}

interface StockAdjustmentInfo {
    id_item_stock_adjustments: number;
    id_projects: number;
    id_warehouses: number;
    id_acc_vouchers?: number | null;
    adjustment_date: string;
    remarks: string | null;
    created_at: string;
    project?: { projectName: string };
    warehouse?: { warehouseName: string };
    adjustmentType?: { adjustmentTypeName: string };
    adjustment_type?: { adjustmentTypeName: string };
    accVoucher?: { id_acc_vouchers: number; voucherNo: string; voucherDate: string; voucherType: string } | null;
    acc_voucher?: { id_acc_vouchers: number; voucherNo: string; voucherDate: string; voucherType: string } | null;
    rows: AdjustmentRow[];
    creator?: { firstName: string; lastName: string };
    files?: {
        id_ref: number;
        refType: string;
        attachmentName: string;
        attachmentPath: string;
        fileSize: number;
        createdAt: string;
        creator: { firstName: string; lastName: string };
    }[];
}

interface RouteParams {
    idItemStockAdjustments: string;
}

export function ItemStockAdjustmentDetails() {
    const [info, setInfo] = useState<StockAdjustmentInfo | null>(null);
    const [error, setError] = useState<string | null>(null);
    const { idItemStockAdjustments }: RouteParams = useParams();
    const jwt = () => {
        const myToken = localStorage.getItem('MyToken');
        return myToken != null ? JSON.parse(myToken) : null;
    };

    useEffect(() => {
        setError(null);
        if (idItemStockAdjustments) {
            fetch(
                process.env.REACT_APP_API_URL + 'item-stock-adjustment/' + idItemStockAdjustments,
                getRequestOptions()
            )
                .then((resp) => resp.json())
                .then((resp) => {
                    if (resp.success && resp.data) {
                        setInfo(resp.data);
                    } else {
                        setError(resp.errorMessage || 'Stock adjustment not found or you do not have access to view it.');
                    }
                })
                .catch((err) => {
                    console.error(err);
                    setError('Failed to load stock adjustment details.');
                });
        }
    }, [idItemStockAdjustments]);

    const numberFormat = (value: any) => {
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        return num.toLocaleString('en-US', {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const getReadableFileSizeString = (fileSizeInBytes: number) => {
        return (fileSizeInBytes / (1024 * 1024)).toFixed(4) + ' MB';
    };

    const handleFileSubmit = (files: any, allFiles: []) => {
        showLoader();
        const postFormData = new FormData();
        postFormData.append('idRef', idItemStockAdjustments.toString());
        postFormData.append('refType', 'item_stock_adjustments');
        postFormData.append('folderPath', 'item-stock-adjustments');
        for (let i = 0; i < files.length; i++) {
            postFormData.append('files[]', files[i].file);
        }

        axios({
            method: 'POST',
            url: `${process.env.REACT_APP_API_URL}file/upload`,
            headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${jwt()}`,
            },
            data: postFormData,
        })
            .then((response: AxiosResponse<any>) => {
                Swal.close();
                allFiles.forEach((f: any) => f.remove());
                if (response?.data?.success === false) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: response?.data?.errorMessage ?? 'File upload failed.',
                    });
                    return;
                }
                fetch(
                    `${process.env.REACT_APP_API_URL}item-stock-adjustment/${idItemStockAdjustments}`,
                    getRequestOptions()
                )
                    .then((resp) => resp.json())
                    .then((resp) => {
                        if (resp.success && resp.data) {
                            setInfo(resp.data);
                        }
                    });
            })
            .catch((error: AxiosError<any>) => {
                Swal.close();
                const errorsMessage: string = validationErrorMsg(error?.response?.data?.errors).join('<br>');
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    html: errorsMessage || error?.response?.data?.errorMessage || 'File upload failed.',
                });
            });
    };

    const getCategoryName = (item: AdjustmentRow['item']) =>
        item?.item_category?.categoryName ?? item?.itemCategory?.categoryName ?? '-';

    const getAdjustmentTypeName = (d: StockAdjustmentInfo) =>
        d?.adjustmentType?.adjustmentTypeName ?? d?.adjustment_type?.adjustmentTypeName ?? '-';

    // Bangladesh format: DD/MM/YYYY or DD/MM/YYYY, hh:mm AM/PM
    const formatDateBD = (dateStr: string | null | undefined, includeTime = false): string => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        const day = d.getDate().toString().padStart(2, '0');
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const year = d.getFullYear();
        if (includeTime) {
            const h = d.getHours();
            const m = d.getMinutes().toString().padStart(2, '0');
            const ampm = h >= 12 ? 'PM' : 'AM';
            const h12 = h % 12 || 12;
            return `${day}/${month}/${year}, ${h12}:${m} ${ampm}`;
        }
        return `${day}/${month}/${year}`;
    };

    return (
        <>
            <div className='card card-custom'>
                <div className='card-header card-header-stretch overflow-auto'>
                    <div className='card-title'>
                        <h3 className='card-label'>Stock Adjustment Details</h3>
                        <small className='text-muted'>| Information about the Stock Adjustment</small>
                    </div>
                </div>
            </div>
            {error && (
                <div className='alert alert-danger d-flex align-items-center'>
                    <span>{error}</span>
                    <Link to='/item-stock-adjustment' className='btn btn-sm btn-light-danger ms-auto'>
                        Back to List
                    </Link>
                </div>
            )}
            {!error && (
            <Row>
                <Col md={4} className='pe-2'>
                    <div className='card card-flush overflow-hidden mt-3 ms-2 me-0'>
                        <div className='card-header py-5'>
                            <h3 className='card-title align-items-start flex-column'>
                                <span className='card-label fw-bold text-dark'>Summary</span>
                            </h3>
                        </div>
                        <div className='card-body pt-0'>
                            <Table striped bordered hover size='sm'>
                                <tbody>
                                    <tr>
                                        <td style={{ width: '50%' }}>ID</td>
                                        <td>{info?.id_item_stock_adjustments}</td>
                                    </tr>
                                    <tr>
                                        <td>Project</td>
                                        <td>{info?.project?.projectName ?? '-'}</td>
                                    </tr>
                                    <tr>
                                        <td>Warehouse</td>
                                        <td>{info?.warehouse?.warehouseName ?? '-'}</td>
                                    </tr>
                                    <tr>
                                        <td>Adjustment Type</td>
                                        <td>
                                            <span className='badge badge-primary'>
                                                {info ? getAdjustmentTypeName(info) : '-'}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Adjustment Date</td>
                                        <td>{formatDateBD(info?.adjustment_date)}</td>
                                    </tr>
                                    <tr>
                                        <td>Remarks</td>
                                        <td>{info?.remarks ?? '-'}</td>
                                    </tr>
                                    <tr>
                                        <td>Voucher</td>
                                        <td>
                                            {(info?.accVoucher ?? info?.acc_voucher) ? (
                                                <Link
                                                    to={`/transaction-details/${(info?.accVoucher ?? info?.acc_voucher)?.id_acc_vouchers}`}
                                                    target="_blank"
                                                    className="btn btn-sm btn-light-primary"
                                                >
                                                    <KTSVG path='/media/svg/icons/General/Clip.svg' className='svg-icon-4 me-1' />
                                                    {(info?.accVoucher ?? info?.acc_voucher)?.voucherNo ?? '-'}
                                                </Link>
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Created By</td>
                                        <td>
                                            {info?.creator
                                                ? `${info.creator.firstName} ${info.creator.lastName} at ${formatDateBD(info.created_at, true)}`
                                                : '-'}
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </div>
                    </div>
                    <div className='card card-flush overflow-hidden mt-3 ms-2 me-0'>
                        <div className='card-header py-5'>
                            <h3 className='card-title'>
                                <KTSVG className='svg-icon-3 me-2' path='/media/svg/icons/General/Clip.svg' />
                                Attach files
                            </h3>
                        </div>
                        <div className='card-body'>
                            <Table striped bordered hover size='sm'>
                                <thead>
                                    <tr>
                                        <th style={{ width: '50%' }}>Attachment</th>
                                        <th style={{ width: '50%' }}>Uploaded By</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(!info?.files || info.files.length === 0) && (
                                        <tr>
                                            <td colSpan={2}>No File Attached</td>
                                        </tr>
                                    )}
                                    {info?.files?.map((attachment, index) => (
                                        <tr key={`attachment-${index}`}>
                                            <td>
                                                <a
                                                    rel='noreferrer'
                                                    href={`${process.env.REACT_APP_AWS_URL}item-stock-adjustments/${attachment.attachmentPath}`}
                                                    className='fs-6 text-hover-primary fw-bold'
                                                    target='_blank'
                                                >
                                                    {attachment.attachmentName}
                                                </a>
                                                <div className='text-gray-400'>
                                                    {getReadableFileSizeString(attachment.fileSize)}
                                                </div>
                                            </td>
                                            <td>
                                                {attachment.creator?.firstName} {attachment.creator?.lastName} at{' '}
                                                {formatDateBD(attachment.createdAt, true)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            <Dropzone
                                styles={{ dropzone: { border: 'none' } }}
                                accept='image/*,audio/*,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,.xls,.xlsx,.csv'
                                onSubmit={handleFileSubmit}
                            />
                        </div>
                    </div>
                </Col>
                <Col md={8} className='pt-2'>
                    <div className='card card-flush overflow-hidden m-2 me-0'>
                        <div className='card-header py-5'>
                            <h3 className='card-title align-items-start flex-column'>
                                <span className='card-label fw-bold text-dark'>Item Details</span>
                            </h3>
                        </div>
                        <div className='card-body pt-0'>
                            <Table striped bordered hover size='sm'>
                                <thead>
                                    <tr>
                                        <th>SL No</th>
                                        <th>Item Name</th>
                                        <th>Item Code</th>
                                        <th>Category</th>
                                        <th>Unit</th>
                                        <th className='text-center'>Prev. Stock</th>
                                        <th className='text-center'>Physical Qty</th>
                                        <th className='text-center'>Adjustment Qty</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!info?.rows?.length && (
                                        <tr>
                                            <td colSpan={8}>No items</td>
                                        </tr>
                                    )}
                                    {info?.rows?.map((row, index) => {
                                        const adjQty =
                                            Number(row.physical_quantity) - Number(row.stock_quantity);
                                        return (
                                            <tr key={row.id_item_stock_adjustment_rows}>
                                                <td>{index + 1}</td>
                                                <td>{row.item?.itemName ?? '-'}</td>
                                                <td>{row.item?.itemCode ?? '-'}</td>
                                                <td>{getCategoryName(row.item)}</td>
                                                <td>{row.item?.unit?.unitName ?? '-'}</td>
                                                <td className='text-center'>
                                                    {numberFormat(row.stock_quantity)}
                                                </td>
                                                <td className='text-center'>
                                                    {numberFormat(row.physical_quantity)}
                                                </td>
                                                <td className='text-center'>
                                                    <span
                                                        className={
                                                            adjQty >= 0
                                                                ? 'badge badge-light-success'
                                                                : 'badge badge-light-danger'
                                                        }
                                                    >
                                                        {adjQty >= 0 ? '+' : ''}
                                                        {numberFormat(adjQty)}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                            <div className='row'>
                                <div className='col text-center'>
                                    <Link
                                        className='btn btn-info btn-sm'
                                        to='/item-stock-adjustment'
                                    >
                                        <KTSVG
                                            path='/media/svg/icons/Media/Back.svg'
                                            className='svg-icon-5 svg-icon'
                                        />{' '}
                                        Back to Stock Adjustment List
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
            )}
        </>
    );
}

export default ItemStockAdjustmentDetails;
